import React from 'react';
import {
  Banner,
  Button,
  CondensedSmall,
  DateUtil,
  Gutter,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  SectionHeader,
  useApi,
  useBreakpoint,
} from '@axiom/ui';
import { useLocation, useNavigate, useParams } from 'react-router';

import { UserApi } from '../../api/user-api';
import { CandidateApi } from '../../api/candidate-api';
import { CandidateUtil } from '../../utils/candidate-util';
import { CalendarApi } from '../../api/calendar-api';
import { PleaseUseDesktopModal } from '../PleaseUseDesktopModal/PleaseUseDesktopModal';

import { InterviewAvailabilityModal } from './InterviewAvailabilityModal';

enum LastUpdateUserTypes {
  TALENT = 'Talent',
  HQ = 'HQ',
}
const AXIOM_DISPLAY_NAME = 'Axiom';

export type InterviewAvailabilityProps = {
  name?: string;
};

export const InterviewAvailability = ({
  name = 'INTERVIEW_AVAILABILITY',
}: InterviewAvailabilityProps = {}) => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const { pathname } = useLocation();
  const showInterviewAvailabilityModal = pathname.includes(
    '/interview-availability'
  );
  const { candidateId: urlCandidateId } = useParams();
  const [{ data: user }] = useApi(UserApi.readUser());
  const candidateId = CandidateUtil.reconcileUserCandidateId(
    user,
    urlCandidateId
  );
  const [{ data: candidate }] = useApi(CandidateApi.readCandidate(candidateId));
  const { id: calendarId = null } = candidate.calendar || {};

  const DEFAULT_CALENDAR_DATA = {
    data: { freeTimeUpdatedAt: null, freeTimeUpdatedUserType: null },
  };
  const [{ data: calendarData } = DEFAULT_CALENDAR_DATA] = useApi(
    calendarId ? CalendarApi.readCalendar(calendarId) : undefined
  );
  const { calculatedDisplayName } = candidate;
  const {
    freeTimeUpdatedAt: lastUpdateAt,
    freeTimeUpdatedUserType: lastUpdateUserType,
  } = calendarData;

  const handleCloseModal = () => {
    void navigate('../');
  };

  const handleOpenModal = () => {
    void navigate('interview-availability');
  };

  return (
    <div data-test={name}>
      <SectionHeader name="INTERVIEW_AVAILABILITY_HEADER">
        Interview Availability
      </SectionHeader>

      <Gutter bottom="16px" />
      {!!candidate.isInterviewAvailabilityOutdated && (
        <>
          <Banner name="TIMED_REMINDER_BANNER" type="critical">
            <ParagraphHeader as="span">
              It’s time to update your availability!
            </ParagraphHeader>
          </Banner>
          <Gutter bottom="16px" />
        </>
      )}
      <Paragraph>
        Update your availability for interviews and get in front of clients even
        faster.
      </Paragraph>

      <Gutter bottom="16px" />

      <Layout position="right">
        <LayoutItem>
          <Button
            variation="outline"
            name="INTERVIEW_AVAILABILITY_BUTTON"
            onClick={handleOpenModal}
          >
            Update
          </Button>
        </LayoutItem>
      </Layout>
      {!!lastUpdateAt && (
        <Gutter top="16px">
          <CondensedSmall name="LAST_UPDATED">
            Last Updated: {DateUtil.displayRelativeTime(lastUpdateAt)} by{' '}
            {lastUpdateUserType === LastUpdateUserTypes.TALENT
              ? calculatedDisplayName
              : AXIOM_DISPLAY_NAME}
          </CondensedSmall>
        </Gutter>
      )}
      {showInterviewAvailabilityModal ? (
        isMobile ? (
          <PleaseUseDesktopModal onClose={handleCloseModal} />
        ) : (
          <InterviewAvailabilityModal
            candidate={candidate}
            calendarData={calendarData}
            user={user}
            onClose={handleCloseModal}
          />
        )
      ) : null}
    </div>
  );
};
